import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from './config';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './config';
import { logger } from '../utils/logger';
import { getRecaptchaToken } from '../utils/recaptcha';

/**
 * Resolve Cloud Functions base URL (mirrors stripeService pattern).
 * @returns {string|null}
 */
function getCloudFunctionBaseUrl() {
  const isProduction =
    typeof window !== 'undefined' &&
    !window.location.hostname.includes('localhost') &&
    !window.location.hostname.includes('127.0.0.1');

  let functionUrl = import.meta.env.VITE_CLOUD_FUNCTION_URL;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'altradenative';

  if (!functionUrl && isProduction && projectId) {
    functionUrl = `https://us-central1-${projectId}.cloudfunctions.net`;
  }

  if (!functionUrl) {
    functionUrl = 'http://localhost:5001';
  }

  return { functionUrl, projectId };
}

/**
 * Build URL for a named HTTPS function.
 * @param {string} name
 * @returns {string}
 */
function getFunctionUrl(name) {
  const { functionUrl, projectId } = getCloudFunctionBaseUrl();
  if (functionUrl.includes('localhost') || functionUrl.includes('127.0.0.1')) {
    return `${functionUrl.replace(/\/$/, '')}/${projectId}/us-central1/${name}`;
  }
  return `${functionUrl.replace(/\/$/, '')}/${name}`;
}

/**
 * Sign up via secure Cloud Function (reCAPTCHA + rate limits + email rules).
 * Free trial / Firestore profile is deferred until email verification.
 * @param {string} email
 * @param {string} password
 * @param {object} userData
 * @returns {Promise<object>}
 */
export const signup = async (email, password, userData = {}) => {
  try {
    let recaptchaToken;
    try {
      recaptchaToken = await getRecaptchaToken('signup');
    } catch (captchaErr) {
      logger.error('[signup] reCAPTCHA failed', captchaErr);
      return {
        success: false,
        error: 'Security check failed. Please refresh the page and try again.',
        errorCode: 'auth/captcha-failed'
      };
    }

    const signupUrl = getFunctionUrl('secureSignup');
    const response = await fetch(signupUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        organization: userData.organization || '',
        riskProfile: userData.riskProfile || 'Neutral',
        recaptchaToken
      })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success) {
      return {
        success: false,
        error: data.error || 'An error occurred during signup',
        errorCode: data.errorCode
      };
    }

    // Sign in briefly with custom token to send Firebase verification email
    if (data.customToken) {
      try {
        const credential = await signInWithCustomToken(auth, data.customToken);
        try {
          await sendEmailVerification(credential.user);
        } catch (verifyErr) {
          logger.warn('[signup] sendEmailVerification failed', verifyErr);
        }
        await signOut(auth);
      } catch (tokenErr) {
        logger.error('[signup] custom token / verification flow failed', tokenErr);
        // Account exists; user can use resend after sign-in
      }
    }

    return {
      success: true,
      user: {
        uid: data.uid,
        email: data.email,
        emailVerified: false,
        ...userData
      }
    };
  } catch (error) {
    logger.error('[signup] unexpected error', error);
    let errorMessage = 'An error occurred during signup';

    if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network error. Please check your connection';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      errorCode: error.code
    };
  }
};

/**
 * After email is verified, create users/{uid} + free trial server-side.
 * Safe to call repeatedly (idempotent).
 * @returns {Promise<object|null>} profile data or null
 */
export const claimUserProfile = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'Not signed in' };
    }

    await user.reload();
    if (!user.emailVerified) {
      return { success: false, error: 'Email not verified', errorCode: 'auth/email-not-verified' };
    }

    const idToken = await user.getIdToken();
    const url = getFunctionUrl('claimUserProfile');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify({})
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      return {
        success: false,
        error: data.error || 'Failed to activate profile',
        errorCode: data.errorCode
      };
    }

    return { success: true, user: data.user, alreadyExists: data.alreadyExists };
  } catch (error) {
    logger.error('[claimUserProfile] error', error);
    return {
      success: false,
      error: error.message || 'Failed to activate profile'
    };
  }
};

/**
 * Sign in an existing user
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise<object>} - User object and success status
 */
export const signin = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );

    const user = userCredential.user;

    // Activate free trial only after verified email
    if (user.emailVerified) {
      await claimUserProfile();
    }

    // Get additional user data from Firestore
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    let additionalData = {};
    if (userDoc.exists()) {
      additionalData = userDoc.data();
    }

    return {
      success: true,
      user: {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        displayName: user.displayName,
        ...additionalData
      }
    };
  } catch (error) {
    let errorMessage = 'An error occurred during sign in';

    if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email';
    } else if (error.code === 'auth/wrong-password') {
      errorMessage = 'Incorrect password';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many failed attempts. Please try again later';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network error. Please check your connection';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled';
    } else if (error.code === 'auth/invalid-credential') {
      errorMessage = 'Invalid email or password';
    }

    return {
      success: false,
      error: errorMessage,
      errorCode: error.code
    };
  }
};

/**
 * Sign out the current user
 * @returns {Promise<object>} - Success status
 */
export const logout = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to sign out'
    };
  }
};

/**
 * Send password reset email
 * @param {string} email - User's email address
 * @returns {Promise<object>} - Success status
 */
export const resetPassword = async (email) => {
  try {
    // Validate email format first
    if (!email || typeof email !== 'string' || email.trim() === '') {
      logger.error('[resetPassword] Empty email provided');
      return {
        success: false,
        error: 'Email address is required',
        errorCode: 'auth/invalid-email'
      };
    }

    // Normalize email (trim whitespace, lowercase)
    const normalizedEmail = email.trim().toLowerCase();
    logger.log('[resetPassword] Normalized email:', normalizedEmail);
    logger.log('[resetPassword] Auth instance:', auth ? 'exists' : 'missing');
    logger.log('[resetPassword] Auth app name:', auth?.app?.name);

    // Try without actionCodeSettings first to see if that's causing issues
    // Some Firebase projects have issues with custom redirect URLs
    logger.log('[resetPassword] Attempting to send password reset email...');

    try {
      // First try with actionCodeSettings
      const redirectUrl = typeof window !== 'undefined'
        ? `${window.location.origin}/signin`
        : undefined;

      const actionCodeSettings = redirectUrl ? {
        url: redirectUrl,
        handleCodeInApp: false,
      } : undefined;

      logger.log('[resetPassword] Using actionCodeSettings:', actionCodeSettings);
      await sendPasswordResetEmail(auth, normalizedEmail, actionCodeSettings);
      logger.log('[resetPassword] Email sent successfully with actionCodeSettings');
    } catch (settingsError) {
      logger.warn('[resetPassword] Failed with actionCodeSettings, trying without:', settingsError);
      // If it fails with settings, try without them
      await sendPasswordResetEmail(auth, normalizedEmail);
      logger.log('[resetPassword] Email sent successfully without actionCodeSettings');
    }

    logger.log('[resetPassword] Password reset email sent successfully to:', normalizedEmail);

    return {
      success: true,
      message: 'Password reset email sent! Please check your inbox and spam folder. The email may take a few minutes to arrive.'
    };
  } catch (error) {
    logger.error('[resetPassword] Error caught:', error);
    logger.error('[resetPassword] Error code:', error.code);
    logger.error('[resetPassword] Error message:', error.message);
    logger.error('[resetPassword] Full error object:', JSON.stringify(error, null, 2));

    let errorMessage = 'Failed to send password reset email';

    if (error.code === 'auth/user-not-found') {
      // For security, don't reveal if email exists or not
      // But still send success message to prevent email enumeration
      logger.warn('[resetPassword] User not found, but returning success for security');
      return {
        success: true,
        message: 'If an account exists with this email, a password reset link has been sent. Please check your inbox and spam folder.'
      };
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address format. Please check and try again.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many password reset requests. Please wait a few minutes before trying again.';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network error. Please check your internet connection and try again.';
    } else if (error.code === 'auth/quota-exceeded') {
      errorMessage = 'Email service temporarily unavailable. Please try again later.';
    } else {
      errorMessage = error.message || 'Failed to send password reset email. Please try again.';
    }

    return {
      success: false,
      error: errorMessage,
      errorCode: error.code
    };
  }
};

/**
 * Get current authenticated user
 * @returns {object|null} - Current user object or null
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Listen to authentication state changes
 * @param {function} callback - Function to call when auth state changes
 * @returns {function} - Unsubscribe function
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Send email verification to current user
 * @returns {Promise<object>} - Success status
 */
export const resendEmailVerification = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'No user is currently signed in'
      };
    }

    if (user.emailVerified) {
      return {
        success: false,
        error: 'Email is already verified'
      };
    }

    await sendEmailVerification(user);

    return {
      success: true,
      message: 'Verification email sent! Please check your inbox and spam folder.'
    };
  } catch (error) {
    logger.error('Resend verification error:', error);
    return {
      success: false,
      error: error.message || 'Failed to send verification email'
    };
  }
};

/**
 * Reload current user to check if email is verified
 * @returns {Promise<object>} - User object with updated verification status
 */
export const reloadUser = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'No user is currently signed in'
      };
    }

    await user.reload();

    // Once verified, ensure trial profile exists
    if (user.emailVerified) {
      await claimUserProfile();
    }

    return {
      success: true,
      user: {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        displayName: user.displayName
      }
    };
  } catch (error) {
    logger.error('Reload user error:', error);
    return {
      success: false,
      error: error.message || 'Failed to reload user'
    };
  }
};
